import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { KeyboardEnum } from '~/component/enums/keyboard.enum';

import { FormControlChildAbstract } from '~/component/form/abstracts/control-child.abstract';
import { generateUid } from '~/core/helpers/uid/uid.helper';

@Component({
  selector: 'app-form-range-input',
  templateUrl: './range-input.component.html',
  styleUrls: ['./range-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormRangeInputComponent),
      multi: true,
    },
  ],
})
export class FormRangeInputComponent extends FormControlChildAbstract<number> {
  @Input() public displayValue: string;
  @Input() public keyboardStep = 10;
  @Input() public label;
  @Input() public max = 100;
  @Input() public min = 0;
  @Input() public step = 1;

  @ViewChild('rail') public rail: ElementRef;
  @ViewChild('slider') public slider: ElementRef;

  private isSliding = false;

  public id: string = generateUid();

  public get labelId(): string {
    return `range-input-label-${this.id}`;
  }

  public get thumbIndent(): number {
    return (this.value * 100) / this.max;
  }

  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  private onMouseUp(): void {
    if (this.isSliding) {
      this.slider.nativeElement.focus();
    }

    this.isSliding = false;
  }

  @HostListener('document:mousemove', ['$event'])
  private onMouseMove(event: MouseEvent): void {
    if (!this.isSliding) {
      return;
    }

    this.setValueFromMousePosition(event.clientX);
  }

  @HostListener('document:touchmove', ['$event'])
  private onTouchMove(event: TouchEvent): void {
    if (!this.isSliding) {
      return;
    }

    this.setValueFromMousePosition(event.touches[0]?.clientX);
  }

  public onRailClick(event: MouseEvent): void {
    this.setValueFromMousePosition(event.clientX);
    this.slider.nativeElement.focus();
  }

  public onSliderKeyDown(event: KeyboardEvent): void {
    if (event.code === KeyboardEnum.ArrowLeft) {
      if (this.value <= this.min) {
        return;
      }

      this.value = this.value - this.step;
    }

    if (event.code === KeyboardEnum.ArrowRight) {
      if (this.value >= this.max) {
        return;
      }

      this.value = this.value + this.step;
    }

    if (event.code === KeyboardEnum.PageDown) {
      if (this.value < this.keyboardStep) {
        this.value = this.min;
        return;
      }

      this.value = this.value - this.keyboardStep;
    }

    if (event.code === KeyboardEnum.PageUp) {
      if (this.value > this.max - this.keyboardStep) {
        this.value = this.max;
        return;
      }

      this.value = this.value + this.keyboardStep;
    }

    if (event.code === KeyboardEnum.Home) {
      this.value = this.min;
    }

    if (event.code === KeyboardEnum.End) {
      this.value = this.max;
    }
  }

  public onSliderMouseDown(event: MouseEvent): void {
    event.preventDefault();

    this.isSliding = true;
  }

  public setValueFromMousePosition(clientX: number): void {
    const { left, right, width } =
      this.rail.nativeElement.getBoundingClientRect();

    let value = Math.round(((clientX - left) * this.max) / width);

    if (value % this.step) {
      return;
    }

    if (clientX > right) {
      value = this.max;
    }

    if (clientX < left) {
      value = this.min;
    }

    this.value = value;
  }
}
